import React, { useState, KeyboardEvent, ChangeEvent } from "react";

export interface SmartListLabelsInputProps {
    labels: string[];
    onLabelsChange: (labels: string[]) => void;
    inputId?: string;
    placeholder?: string;
    helperText?: string;
    maxLabels?: number;
    disabled?: boolean;
}

const normalizeLabel = (label: string) => label.trim().toLowerCase();

export const SmartListLabelsInput: React.FC<SmartListLabelsInputProps> = ({
    labels,
    onLabelsChange,
    inputId,
    placeholder = "Type a label and press Enter",
    helperText,
    maxLabels = 1,
    disabled = false,
}) => {
    const [inputValue, setInputValue] = useState("");

    const commitLabel = (rawLabel: string) => {
        const normalized = normalizeLabel(rawLabel);
        if (!normalized) {
            return;
        }

        if (maxLabels === 1) {
            onLabelsChange([normalized]);
            return;
        }

        if (!labels.includes(normalized) && labels.length < maxLabels) {
            onLabelsChange([...labels, normalized]);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            commitLabel(inputValue);
            setInputValue("");
            return;
        }

        if (event.key === "Backspace" && inputValue.length === 0 && labels.length > 0) {
            event.preventDefault();
            onLabelsChange(labels.slice(0, -1));
        }
    };

    const handleRemove = (labelToRemove: string) => {
        if (disabled) {
            return;
        }
        onLabelsChange(labels.filter((label) => label !== labelToRemove));
    };

    return (
        <>
            <div className="smart-list-labels-input">
                {labels.map((label) => (
                    <span key={label} className="smart-list-label-chip">
                        {label}
                        <button
                            type="button"
                            className="smart-list-label-remove"
                            onClick={() => handleRemove(label)}
                            aria-label={`Remove label ${label}`}
                            disabled={disabled}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    id={inputId}
                    type="text"
                    value={inputValue}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setInputValue(event.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="smart-list-labels-field"
                    autoComplete="off"
                    disabled={disabled}
                />
            </div>
            {helperText && <p className="field-helper">{helperText}</p>}
        </>
    );
};
